import { useParams } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { useGetProjectMembersService } from "@/service-hooks/use-get-project-members-service";
import { ProjectMemberRole } from "@/types/project";
import { useUser } from "@/hooks/use-user";

export function useCurrentProjectRole(projectId?: string, enabled: boolean = true) {
  const { id: paramId } = useParams();
  const id = projectId ?? paramId ?? "";
  const auth = useAuth();
  const email = auth.user?.profile?.email as string | undefined;

  const { user } = useUser();
  // A system-level VIEWER is globally read-only across every project, mirroring
  // the backend write-block. It overrides any project membership role, so even a
  // project Owner/Admin who is a system Viewer cannot edit.
  const isSystemViewer = user?.role === "VIEWER";

  const { data: members, isLoading } = useGetProjectMembersService(id, !!id && enabled);

  const role = members?.find((m) => m.user?.email === email)?.role as
    | ProjectMemberRole
    | undefined;

  const isOwner = role === ProjectMemberRole.OWNER;
  const isAdmin = role === ProjectMemberRole.ADMIN;
  const isViewer = role === ProjectMemberRole.VIEWER || isSystemViewer;
  // A platform admin may edit any project, as the API allows (project-authorization.ts lets UserRole.ADMIN past
  // the project-role check). Like the projects list's Delete/Import rule, a platform admin who is explicitly a
  // Viewer member of this project stays read-only.
  const isPlatformAdmin = user?.role === "ADMIN";
  const canEdit =
    (isOwner || isAdmin || (isPlatformAdmin && role !== ProjectMemberRole.VIEWER)) && !isSystemViewer;

  return { role, isOwner, isAdmin, isViewer, isSystemViewer, canEdit, isLoading };
}
